import { Button, Card, Spinner } from '@routly/ui';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { useAuth } from '@/hooks/useAuth';

/**
 * The gate: waiting for the session, failing to get one, or moving on.
 *
 * This is the first screen anyone sees, and it used to be the least like the
 * product — a raw `text-red-500` heading, a `bg-primary text-black` button
 * built out of utility classes instead of the shared one, and a spinning icon
 * where the product turns a ring. Black text on the brand green also stayed
 * black whatever the theme did around it.
 */

export const Route = createFileRoute('/')({
  component: IndexPage,
});

/** Long enough that a slow network is not mistaken for a stuck one. */
const SLOW_AFTER_MS = 10_000;

function useBypassAuth() {
  return (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    window.location.search.includes('bypass=true')
  );
}

function IndexPage() {
  const { isAuthenticated, isLoading, error } = useAuth();
  const [isSlow, setIsSlow] = useState(false);
  const bypassAuth = useBypassAuth();

  useEffect(() => {
    if (!isLoading || bypassAuth) return;
    const timer = window.setTimeout(() => setIsSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [isLoading, bypassAuth]);

  if (bypassAuth) {
    return <Navigate to="/organizations" search={{ bypass: 'true' }} />;
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="flex max-w-md flex-col items-center gap-3 px-6 py-14 text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-5 w-5 text-destructive" />
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">Could not check your session</p>
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">{error.message}</p>
          </div>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <Spinner label="Checking your session" />
        {isSlow ? (
          <p className="text-sm text-muted-foreground">This is taking longer than it should.</p>
        ) : null}
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return <Navigate to="/organizations" />;
}
